interface FileTypeConfig {
	description: string;
	mimeType: string;
	extension: string;
}

/**
 * Saves a blob to a file using the File System Access API (showSaveFilePicker) when available,
 * falling back to a classic <a> tag download.
 *
 * A failed download **propagates to the caller on both paths**. It used to be swallowed by an
 * internal `.catch(console.error)` on the picker path, so a rejected export produced no toast at
 * all on Chrome/Edge — the spinner stopped and nothing happened. That is why a broken Contacts
 * export went unreported and left no trace in the logs (PREC-3953).
 *
 * The one thing that is not an error is the user dismissing the save dialog (`AbortError`): it
 * resolves quietly, so cancelling never shows a failure.
 */
export async function saveFileFromBlob(
	blobPromise: Promise<Blob>,
	fileName: string,
	fileType: FileTypeConfig
): Promise<void> {
	// @ts-expect-error - showSaveFilePicker is not typed
	const showSaveFilePicker = window.showSaveFilePicker;

	if (!showSaveFilePicker) {
		const blob = await blobPromise;
		const url = URL.createObjectURL(blob);

		const a = document.createElement("a");

		a.href = url;
		a.download = fileName;
		a.rel = "noopener";

		document.body.appendChild(a);
		a.click();
		a.remove();

		setTimeout(() => URL.revokeObjectURL(url), 1000);

		return;
	}

	// The download runs while the save dialog is open. Settle it into a value now so a rejection
	// cannot surface as an `unhandledrejection` during the seconds the dialog is up; it is
	// re-thrown below, once the user has chosen a destination.
	const settled = blobPromise.then(
		(blob) => ({ blob, error: undefined }),
		(error: unknown) => ({ blob: undefined, error })
	);

	let handle: { createWritable: () => Promise<FileSystemWritableFileStream> };

	try {
		handle = await showSaveFilePicker({
			suggestedName: fileName,
			types: [
				{
					description: fileType.description,
					accept: { [fileType.mimeType]: [`.${fileType.extension}`] },
				},
			],
		});
	} catch (error) {
		await settled;

		if (error instanceof DOMException && error.name === "AbortError") {
			return;
		}

		throw error;
	}

	const { blob, error } = await settled;

	// Thrown before `createWritable`, which would otherwise truncate the file the user picked.
	if (error !== undefined) {
		throw error;
	}

	const writable = await handle.createWritable();

	await writable.write(blob as Blob);
	await writable.close();
}
